"use client";

import { useChat } from "@ai-sdk/react";
import { X } from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useMemo, useState, useRef } from "react";
import { ModelSelector } from "@/components/model-selector";
import { AuthDialog } from "@/components/playground/auth-dialog";
import { ChatSidebar } from "@/components/playground/chat-sidebar";
import { GroupChatUI } from "@/components/playground/group-chat-ui";
import { SidebarProvider } from "@/components/ui/sidebar";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useUser } from "@/hooks/useUser";
import { useFetchClient } from "@/lib/fetch-client";
import { mapModels } from "@/lib/mapmodels";
import { shouldDisableFallback } from "@/lib/no-fallback";
import { getProviderIcon } from "@llmgateway/shared/components";
import type { ApiModel, ApiProvider } from "@/lib/fetch-models";
import type { ComboboxModel, Organization, Project } from "@/lib/types";
interface GroupChatClientProps {
  models: ApiModel[];
  providers: ApiProvider[];
  organizations: Organization[];
  selectedOrganization: Organization | null;
  projects: Project[];
  selectedProject: Project | null;
}
interface GroupMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  model?: string;
  timestamp: number;
}
export default function GroupChatClient({
  models,
  providers,
  organizations,
  selectedOrganization,
  projects,
  selectedProject
}: GroupChatClientProps) {
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const posthog = usePostHog();
  const fetchClient = useFetchClient();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Filter out image/video/embedding models — group chat is text-only
  const chatModels = useMemo(() => models.filter(m => !m.output?.includes("image") && !m.output?.includes("video") && !m.output?.includes("embedding")), [models]);
  const mapped = useMemo(() => mapModels(chatModels, providers), [chatModels, providers]);
  const availableModels: ComboboxModel[] = mapped;
  const [selectedModels, setSelectedModels] = useState<string[]>([]);
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentChatId] = useState<string | null>(null);
  const [initialPrompt, setInitialPrompt] = useState("");
  const turnsPerRound = 5;
  const turnCounterRef = useRef(0);
  const tempMessageIdRef = useRef<string | null>(null);
  const lastAssistantTextRef = useRef<string>("");
  const stoppedRef = useRef<boolean>(false);
  const isAuthenticated = !isUserLoading && !!user;
  const showAuthDialog = !isAuthenticated && !isUserLoading && !user;
  const returnUrl = useMemo(() => {
    const search = searchParams.toString();
    return search ? `${pathname}?${search}` : pathname;
  }, [pathname, searchParams]);
  const addModel = (modelId: string) => {
    if (selectedModels.length < 5 && !selectedModels.includes(modelId)) {
      setSelectedModels([...selectedModels, modelId]);
    }
  };
  const removeModel = (modelId_0: string) => {
    setSelectedModels(selectedModels.filter(m_0 => m_0 !== modelId_0));
  };

  // AI SDK chat hook (we'll drive turns manually and map streaming into our UI)
  const {
    messages: aiMessages,
    setMessages: setAiMessages,
    sendMessage,
    stop
  } = useChat({
    onError: e => {
      setError(e.message);
      stoppedRef.current = true;
    },
    onFinish: ({
      message
    }) => {
      // Capture final assistant text for chaining to next turn
      const textContent = message.parts.filter(p => p.type === "text").map(p_0 => p_0.text).join("");
      lastAssistantTextRef.current = textContent;
      const finishedMessageId = tempMessageIdRef.current;
      setMessages(prev => prev.map(entry => entry.id === finishedMessageId ? {
        ...entry,
        content: textContent
      } : entry));
    }
  });

  // Reflect AI SDK streaming into the temporary assistant message for current turn
  useEffect(() => {
    const messageId = tempMessageIdRef.current;
    const turn = aiMessages.findLast(message_0 => message_0.role === "user");
    if (!messageId || turn?.id !== messageId) {
      return;
    }
    // Find latest assistant message text
    const last = aiMessages.at(-1);
    if (!last || last.role !== "assistant") {
      return;
    }
    const text = (last.parts || []).filter(p_1 => p_1.type === "text").map(p_2 => p_2.text).join("");
    setMessages(prev_0 => prev_0.map(msg => msg.id === messageId ? {
      ...msg,
      content: text
    } : msg));
  }, [aiMessages]);
  useEffect(() => () => {
    stoppedRef.current = true;
    void stop();
  }, [stop]);
  const startConversation = async () => {
    if (isStreaming || !selectedProject || !initialPrompt.trim() || selectedModels.length < 2) {
      return;
    }
    setError(null);
    setIsStreaming(true);
    stoppedRef.current = false;
    posthog.capture("playground_group_chat_started", {
      models: selectedModels,
      model_count: selectedModels.length
    });
    try {
      const {
        data,
        error: keyError
      } = await fetchClient.POST("/playground/ensure-key", {
        body: {
          projectId: selectedProject.id
        }
      });
      if (keyError || !data) {
        throw new Error("Could not prepare this organization for chat. Please try again.");
      }
      if (stoppedRef.current) {
        return;
      }
      if (messages.length === 0) {
        setAiMessages([]);
        turnCounterRef.current = 0;
        lastAssistantTextRef.current = "";
        setMessages([{
          id: crypto.randomUUID(),
          role: "user",
          content: initialPrompt,
          timestamp: Date.now()
        }]);
      }
      const roundEnd = turnCounterRef.current + turnsPerRound;
      while (!stoppedRef.current && turnCounterRef.current < roundEnd) {
        const currentModel = selectedModels[turnCounterRef.current % selectedModels.length]!;
        const messageId_0 = crypto.randomUUID();
        tempMessageIdRef.current = messageId_0;
        setMessages(prev_1 => [...prev_1, {
          id: messageId_0,
          role: "assistant",
          content: "",
          model: currentModel,
          timestamp: Date.now()
        }]);
        const instruction = lastAssistantTextRef.current ? `You are ${currentModel}, a member of an AI council discussing: "${initialPrompt}". Read the discussion so far and challenge the previous argument. Add a concrete counterargument or an overlooked tradeoff. Keep your contribution concise. Previous argument: "${lastAssistantTextRef.current}"` : `You are ${currentModel}, opening an AI council discussion on: "${initialPrompt}". Take a clear stance and present a concise argument with a concrete reason. Other models will challenge your position.`;
        await sendMessage({
          id: messageId_0,
          role: "user",
          parts: [{
            type: "text",
            text: instruction
          }]
        }, {
          headers: shouldDisableFallback(currentModel) ? {
            "x-no-fallback": "true"
          } : {},
          body: {
            model: currentModel,
            apiKey: data.token,
            project_id: selectedProject.id
          }
        });
        turnCounterRef.current += 1;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "The discussion could not continue.");
    } finally {
      const unfinishedId = tempMessageIdRef.current;
      setMessages(previous => previous.filter(message => message.id !== unfinishedId || message.content.trim().length > 0));
      tempMessageIdRef.current = null;
      setIsStreaming(false);
    }
  };
  const stopConversation = () => {
    stoppedRef.current = true;
    void stop();
  };
  const clearConversation = () => {
    setMessages([]);
    setAiMessages([]);
    setInitialPrompt("");
    turnCounterRef.current = 0;
    setError(null);
    stoppedRef.current = false;
    lastAssistantTextRef.current = "";
  };
  const handleSelectOrganization = (org: Organization | null) => {
    const params = new URLSearchParams(Array.from(searchParams.entries()));
    if (org?.id) {
      params.set("orgId", org.id);
    } else {
      params.delete("orgId");
    }
    params.delete("projectId");
    router.push(params.toString() ? `/group?${params.toString()}` : "/group");
  };
  const handleOrganizationCreated = (org_0: Organization) => {
    const params_0 = new URLSearchParams(Array.from(searchParams.entries()));
    params_0.set("orgId", org_0.id);
    params_0.delete("projectId");
    router.push(params_0.toString() ? `/group?${params_0.toString()}` : "/group");
  };
  const handleSelectProject = (project: Project | null) => {
    if (!project) {
      return;
    }
    const params_1 = new URLSearchParams(Array.from(searchParams.entries()));
    params_1.set("orgId", project.organizationId);
    params_1.set("projectId", project.id);
    router.push(params_1.toString() ? `/group?${params_1.toString()}` : "/group");
  };
  const handleProjectCreated = (project_0: Project) => {
    const params_2 = new URLSearchParams(Array.from(searchParams.entries()));
    params_2.set("orgId", project_0.organizationId);
    params_2.set("projectId", project_0.id);
    router.push(params_2.toString() ? `/group?${params_2.toString()}` : "/group");
  };
  return <SidebarProvider>
			<div className="flex h-svh bg-background w-full overflow-hidden">
				<ChatSidebar onNewChat={clearConversation} onChatSelect={() => {}} currentChatId={currentChatId ?? undefined} isLoading={isStreaming} organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={handleSelectOrganization} onOrganizationCreated={handleOrganizationCreated} projects={projects} selectedProject={selectedProject} onSelectProject={handleSelectProject} onProjectCreated={handleProjectCreated} />
				<div className="flex flex-1 flex-col w-full min-h-0 overflow-hidden">
					<header className="shrink-0 flex items-center p-4 border-b bg-background">
						<div className="flex items-center gap-3 min-w-0 flex-1">
							<SidebarTrigger />
							<div className="flex items-center gap-2 flex-1 min-w-0">
								<h2 className="text-lg font-semibold whitespace-nowrap">
									Group Chat · Council
								</h2>
							</div>
						</div>
					</header>

					<div className="flex-1 flex flex-col min-h-0 w-full max-w-4xl mx-auto overflow-hidden">
						{/* Model Selection Panel */}
						<div className="shrink-0 p-4 border-b bg-muted/30">
							<div className="space-y-3">
								<div>
									<h2 className="text-sm font-medium mb-4">
										Your council ({selectedModels.length}/5)
									</h2>
									{selectedModels.length < 5 && <fieldset disabled={isStreaming}>
											<ModelSelector models={chatModels} providers={providers} value="" onValueChange={value => {
                    if (value) {
                      addModel(value);
                    }
                  }} placeholder="Invite a model…" showRegionalVariants />
										</fieldset>}
								</div>

								{selectedModels.length > 0 && <div className="flex flex-wrap gap-2">
										{selectedModels.map(modelId_1 => {
                  const model = availableModels.find(m_1 => m_1.id === modelId_1);
                  const providerDef = providers.find(p_3 => p_3.id === model?.providerId);
                  const ProviderIcon = providerDef ? getProviderIcon(providerDef.id) : null;
                  const bgTint = providerDef?.color ? `${providerDef.color}15` : undefined;
                  const borderTint = providerDef?.color ? `${providerDef.color}30` : undefined;
                  return <div key={modelId_1} className="flex items-center gap-2 pl-2 pr-1 py-1.5 border rounded-full text-sm" style={{
                    backgroundColor: bgTint,
                    borderColor: borderTint
                  }}>
													{ProviderIcon ? <ProviderIcon className="h-3.5 w-3.5" style={{
                      color: providerDef?.color ?? undefined
                    }} /> : null}
													<span className="max-w-[220px] truncate">
														{model?.name ?? modelId_1}
													</span>
													<button type="button" onClick={() => removeModel(modelId_1)} disabled={isStreaming} className="rounded-full hover:bg-foreground/10 transition-colors p-1" aria-label={`Remove ${model?.name ?? modelId_1}`}>
														<X className="size-3" />
													</button>
												</div>;
                })}
									</div>}

								{selectedModels.length === 0 && <p className="text-sm text-muted-foreground">
										Choose 2–5 models. Each gets a turn at the table.
									</p>}
							</div>
						</div>

						<GroupChatUI messages={messages} isStreaming={isStreaming} error={error} initialPrompt={initialPrompt} setInitialPrompt={setInitialPrompt} onStart={startConversation} onStop={stopConversation} onClear={clearConversation} selectedModels={selectedModels} availableModels={availableModels} canStart={isAuthenticated && !!selectedProject && selectedModels.length >= 2 && !isStreaming} />
					</div>
				</div>
			</div>
			<AuthDialog open={showAuthDialog} returnUrl={returnUrl} />
		</SidebarProvider>;
}